/**
 * Field / reservoir production totals — oil, liquid, injection, Np, water cut.
 * Three Y-axes; year domain shared with sibling charts via xDomain + FIELD_CHART_LAYOUT.
 */
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { MaterialBalanceFieldPoint } from '@/types/materialBalance';

export const FIELD_COLORS = {
  oil: '#212121',
  liquid: '#1565c0',
  injection: '#2e7d32',
  waterCut: '#c62828',
  np: '#6d4c41',
  prodWells: '#2e7d32',
  injWells: '#1565c0',
} as const;

/** Shared Recharts margin so Production + Active wells X-axes line up. */
export const FIELD_CHART_MARGIN = { top: 10, right: 4, left: 0, bottom: 4 } as const;

/** Width of vertical side captions flanking the plot. */
export const FIELD_SIDE_LABEL_W = 22;

/** Inner Y-axis tick band widths (must match both charts). */
export const FIELD_Y_AXIS = {
  left: 44,
  /** WC band — keep enough width so ticks do not sit on the Np band */
  rightWc: 40,
  rightNp: 48,
} as const;

export function niceCeil(value: number): number {
  if (!(value > 0) || !Number.isFinite(value)) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = 10 ** exp;
  const frac = value / base;
  const niceFrac = frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10;
  return niceFrac * base;
}

export function formatYTick(v: number): string {
  if (!Number.isFinite(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 100 || Number.isInteger(v)) return String(Math.round(v));
  if (abs >= 10) return (Math.round(v * 10) / 10).toString();
  return (Math.round(v * 100) / 100).toString();
}

export function AxisSideLabel({
  children,
  color,
  side,
}: {
  children: React.ReactNode;
  color?: string;
  side: 'left' | 'right';
}) {
  return (
    <Typography
      variant="caption"
      component="div"
      sx={{
        writingMode: 'vertical-rl',
        transform: side === 'left' ? 'rotate(180deg)' : 'none',
        color: color || 'text.secondary',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.04,
        lineHeight: 1.15,
        textAlign: 'center',
        userSelect: 'none',
        flexShrink: 0,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: FIELD_SIDE_LABEL_W,
        px: 0,
      }}
    >
      {children}
    </Typography>
  );
}

/** Invisible spacer matching AxisSideLabel width (for axis sync). */
export function AxisSideSpacer() {
  return <Box sx={{ width: FIELD_SIDE_LABEL_W, flexShrink: 0 }} />;
}

/** Spacer matching a manual right tick band (Active wells chart sync). */
export function RightTickBandSpacer({ width }: { width: number }) {
  return <Box sx={{ width, flexShrink: 0 }} />;
}

/**
 * Manual right-side tick band — order is fully under our control (Recharts 3
 * sorts dual right YAxes by yAxisId and overlays them unreliably).
 */
export function ManualYAxisBand({
  max,
  width,
  color,
}: {
  max: number;
  width: number;
  color: string;
}) {
  const ticks = useMemo(() => {
    const n = 5;
    const m = max > 0 ? max : 1;
    return Array.from({ length: n }, (_, i) => (m * i) / (n - 1));
  }, [max]);

  return (
    <Box
      sx={{
        width,
        alignSelf: 'stretch',
        flexShrink: 0,
        position: 'relative',
        borderLeft: `1px solid ${color}`,
        boxSizing: 'border-box',
        // Match LineChart top/bottom margin so ticks line up with the plot
        pt: `${FIELD_CHART_MARGIN.top}px`,
        pb: `${FIELD_CHART_MARGIN.bottom}px`,
      }}
    >
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        {ticks.map((t) => (
          <Typography
            key={t}
            component="div"
            sx={{
              position: 'absolute',
              left: 0,
              right: 2,
              top: max > 0 ? `${(1 - t / max) * 100}%` : '100%',
              transform: 'translateY(-50%)',
              fontSize: 10,
              color,
              textAlign: 'right',
              lineHeight: 1,
              pr: 0.25,
              userSelect: 'none',
            }}
          >
            {formatYTick(t)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}

const LEGEND_ITEMS: { id: string; color: string; dash?: string }[] = [
  { id: 'oil', color: FIELD_COLORS.oil },
  { id: 'liquid', color: FIELD_COLORS.liquid },
  { id: 'injection', color: FIELD_COLORS.injection },
  { id: 'water cut', color: FIELD_COLORS.waterCut, dash: '4 3' },
  { id: 'Np', color: FIELD_COLORS.np },
];

export interface FieldProductionChartProps {
  field: MaterialBalanceFieldPoint[] | null | undefined;
  xDomain: [number, number] | null;
  height?: number;
  /** Full header override; otherwise `{label} · {year}: …` */
  title?: string;
  /** Prefix when title is omitted (e.g. Field total / Reservoir total) */
  label?: string;
  emptyHint?: string;
  extractableOilKt?: number | null;
  onOptimize?: () => void;
  optimizeDisabled?: boolean;
}

export const FieldProductionChart: React.FC<FieldProductionChartProps> = ({
  field,
  xDomain,
  height = 300,
  title,
  label = 'Field total',
  emptyHint = 'No series yet. Run material balance on Reservoir configuration.',
  extractableOilKt = null,
  onOptimize,
  optimizeDisabled = false,
}) => {
  const data = useMemo(
    () =>
      (field || []).map((r) => ({
        year: r.year,
        oil: r.oil,
        liquid: r.liq,
        injection: r.inj,
        Np: r.np,
        'water cut': r.fw ?? 0,
      })),
    [field],
  );

  const ratesMax = useMemo(() => {
    let m = 0;
    for (const r of field || []) m = Math.max(m, r.oil, r.liq, r.inj);
    return niceCeil(m * 1.08 || 1);
  }, [field]);

  const npMaxRaw = useMemo(() => {
    let m = 0;
    for (const r of field || []) m = Math.max(m, r.np);
    return m;
  }, [field]);

  const npMax = useMemo(() => {
    const ceiling = Math.max(npMaxRaw, extractableOilKt ?? 0);
    return niceCeil(ceiling * 1.08 || 1);
  }, [npMaxRaw, extractableOilKt]);

  const npExceedsExtractable =
    extractableOilKt != null && extractableOilKt > 0 && npMaxRaw > extractableOilKt + 1e-6;
  const last = field?.length ? field[field.length - 1] : null;

  const headerTitle =
    title ??
    (last
      ? `${label} · ${last.year}: oil ${last.oil.toFixed(1)} · liq ${last.liq.toFixed(1)} · inj ${last.inj.toFixed(1)} · Np ${last.np.toFixed(1)} · WC ${last.fw != null ? last.fw.toFixed(1) : '—'}%`
      : label);

  if (!data.length) {
    return (
      <Box
        sx={{
          height,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 0.5 }}
        >
          <Typography variant="caption" color="text.secondary">
            {emptyHint}
          </Typography>
          {onOptimize && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled
              sx={{ flexShrink: 0, minWidth: 0, px: 1.25, py: 0.25, fontSize: 11 }}
            >
              Optimize
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  const chartH = height - (npExceedsExtractable ? 64 : 36);
  const legendH = 22;
  const plotRowH = chartH - legendH;

  return (
    <Box
      sx={{
        minHeight: height,
        border: 1,
        borderColor: npExceedsExtractable ? 'warning.main' : 'divider',
        borderRadius: 1,
        p: 0.75,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={1}
        sx={{ px: 0.25, mb: 0.5 }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', minWidth: 0 }}>
          {headerTitle}
          {extractableOilKt != null && (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 1, color: FIELD_COLORS.np }}
            >
              extractable oil {extractableOilKt.toFixed(1)} kt
            </Typography>
          )}
        </Typography>
        {onOptimize && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            disabled={optimizeDisabled}
            onClick={onOptimize}
            sx={{ flexShrink: 0, minWidth: 0, px: 1.25, py: 0.25, fontSize: 11 }}
          >
            Optimize
          </Button>
        )}
      </Stack>

      {npExceedsExtractable && extractableOilKt != null && (
        <Alert severity="warning" sx={{ mx: 0.25, mb: 0.5, py: 0 }}>
          Cumulative oil (Np {npMaxRaw.toFixed(1)} kt) exceeds extractable oil (
          {extractableOilKt.toFixed(1)} kt = recovery factor × OOIP)
        </Alert>
      )}

      {/* L→R: left label · plot · WC ticks · Np ticks · WC caption · Np caption */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          height: plotRowH,
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <AxisSideLabel side="left">rates kt/yr · inj 10³ m³/yr</AxisSideLabel>
        <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ ...FIELD_CHART_MARGIN }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
              <XAxis
                dataKey="year"
                type="number"
                domain={xDomain || ['dataMin', 'dataMax']}
                allowDataOverflow
                tick={{ fontSize: 10 }}
              />
              <YAxis
                yAxisId="rates"
                type="number"
                domain={[0, ratesMax]}
                tick={{ fontSize: 10 }}
                tickFormatter={formatYTick}
                width={FIELD_Y_AXIS.left}
                axisLine={{ stroke: '#455a64' }}
                tickLine={{ stroke: '#455a64' }}
              />
              {/* Hidden — used only for scale; visible ticks are ManualYAxisBand */}
              <YAxis yAxisId="wc" orientation="right" hide domain={[0, 100]} width={0} />
              <YAxis yAxisId="np" orientation="right" hide domain={[0, npMax]} width={0} />
              {extractableOilKt != null && extractableOilKt > 0 && (
                <ReferenceLine
                  yAxisId="np"
                  y={extractableOilKt}
                  stroke={FIELD_COLORS.np}
                  strokeDasharray="6 4"
                  strokeWidth={1.75}
                  label={{
                    value: 'extractable oil',
                    position: 'insideBottomLeft',
                    fill: FIELD_COLORS.np,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                />
              )}
              <Tooltip
                formatter={(value, name) => {
                  if (typeof value !== 'number') return [String(value ?? ''), String(name ?? '')];
                  const n = String(name ?? '');
                  if (n.toLowerCase().includes('water cut')) return [`${value.toFixed(1)}%`, n];
                  return [value.toFixed(2), n];
                }}
              />
              <Line
                yAxisId="rates"
                type="monotone"
                dataKey="oil"
                name="oil"
                stroke={FIELD_COLORS.oil}
                strokeWidth={2.25}
                isAnimationActive={false}
                dot={false}
              />
              <Line
                yAxisId="rates"
                type="monotone"
                dataKey="liquid"
                name="liquid"
                stroke={FIELD_COLORS.liquid}
                strokeWidth={2.25}
                isAnimationActive={false}
                dot={false}
              />
              <Line
                yAxisId="rates"
                type="monotone"
                dataKey="injection"
                name="injection"
                stroke={FIELD_COLORS.injection}
                strokeWidth={2.25}
                isAnimationActive={false}
                dot={false}
              />
              <Line
                yAxisId="wc"
                type="monotone"
                dataKey="water cut"
                name="water cut"
                stroke={FIELD_COLORS.waterCut}
                strokeDasharray="4 3"
                strokeWidth={2}
                isAnimationActive={false}
                dot={false}
              />
              <Line
                yAxisId="np"
                type="monotone"
                dataKey="Np"
                name="Np"
                stroke={FIELD_COLORS.np}
                strokeWidth={2.25}
                isAnimationActive={false}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
        <ManualYAxisBand max={100} width={FIELD_Y_AXIS.rightWc} color={FIELD_COLORS.waterCut} />
        <ManualYAxisBand max={npMax} width={FIELD_Y_AXIS.rightNp} color={FIELD_COLORS.np} />
        <AxisSideLabel side="right" color={FIELD_COLORS.waterCut}>
          water cut
        </AxisSideLabel>
        <AxisSideLabel side="right" color={FIELD_COLORS.np}>
          Np, kt
        </AxisSideLabel>
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="center"
        alignItems="center"
        sx={{ height: legendH, mt: 0.25, flexWrap: 'wrap' }}
      >
        {LEGEND_ITEMS.map((item) => (
          <Stack key={item.id} direction="row" alignItems="center" spacing={0.5}>
            <Box
              sx={{
                width: 16,
                height: 0,
                borderTop: item.dash
                  ? `2px dashed ${item.color}`
                  : `2.25px solid ${item.color}`,
              }}
            />
            <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
              {item.id}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
